import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import React from 'react'

import { StatusChip, StatusTone } from 'components/common/StatusChip'
import { SOURCE_BADGE_COLORS } from 'components/exceptions/styleTokens'
import T from 'T'

import type { ExceptionShape } from './types'
import type { DetectionSource } from 'components/exceptions/types'

// Review status + origin + billing chips for the exception card. Each chip renders only
// when its field is present on the payload (billed is still a BE gap and stays hidden).
type ExceptionMetaChipsProps = {
  exception: ExceptionShape
}

const STATUS_TONE: Record<string, StatusTone> = {
  pending: 'pending',
  confirmed: 'confirmed',
  rejected: 'rejected',
}

const STATUS_LABEL: Record<string, string> = {
  pending: T.PENDING,
  confirmed: T.CONFIRMED,
  rejected: T.REJECTED,
}

// BE sends the raw `source` enum from hh_work_order_exception (collapsed BE enum:
// MOBILE, VISION, CRM, RFID, EDGE_COMPUTE); presentation mapping is owned here. Keys are
// compared lowercase. RFID is truck-side hardware → Driver; EDGE_COMPUTE is automated
// detection → Vision. crm_wo/crm_rm kept as defensive aliases for any pre-collapse rows.
const SOURCE_ALIAS: Record<string, DetectionSource> = {
  ml: 'ml',
  vision: 'ml',
  edge_compute: 'ml',
  driver: 'driver',
  mobile: 'driver',
  rfid: 'driver',
  manual: 'manual',
  crm: 'manual',
  crm_wo: 'manual',
  crm_rm: 'manual',
  web: 'manual',
  office: 'manual',
}

const SOURCE_LABEL: Record<DetectionSource, string> = {
  ml: T.VISION,
  driver: T.DRIVER,
  manual: T.MANUAL,
}

const sourceBadgeSx = (style: { bg: string; color: string }) => ({
  alignItems: 'center',
  bgcolor: style.bg,
  borderRadius: 0.5,
  color: style.color,
  display: 'inline-flex',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.4px',
  px: 0.75,
  py: 0.25,
  textTransform: 'uppercase',
})

const toLowerStringKey = (value: unknown): string => (typeof value === 'string' ? value.toLowerCase() : '')

const ExceptionMetaChips: React.FC<ExceptionMetaChipsProps> = ({ exception }) => {
  const statusKey = toLowerStringKey(exception.confirmationStatus ?? exception.status)
  const statusTone = STATUS_TONE[statusKey]
  const sourceRaw = toLowerStringKey(exception.source)
  const sourceKey = sourceRaw ? SOURCE_ALIAS[sourceRaw] : undefined
  const { billed } = exception

  if (!statusTone && !sourceKey && billed == null) return null

  return (
    <Stack alignItems="center" direction="row" flexWrap="wrap" gap={0.5}>
      {statusTone && <StatusChip label={STATUS_LABEL[statusKey]} showCheck={statusKey === 'confirmed'} tone={statusTone} />}
      {sourceKey && <Box sx={sourceBadgeSx(SOURCE_BADGE_COLORS[sourceKey])}>{SOURCE_LABEL[sourceKey]}</Box>}
      {billed != null && <StatusChip label={billed ? T.BILLED : T.UNBILLED} tone={billed ? 'info' : 'neutral'} />}
    </Stack>
  )
}

export default ExceptionMetaChips
